// Region membership — point-in-box and box-overlap, matching the backend's
// real hazards/regions.py classification exactly (see api/types.ts's
// RegionDef comment for the confirmed live contract: min_lat/max_lat/
// min_lon/max_lon, no nearest-anchor matching, no distance cutoff).
//
// This REPLACES the short-lived anchor/nearest-point model from earlier
// 2026-09-20 — that call was reversed directly (regions are not
// anchorpoints, they are bbox areas), and the real bbox contract landed
// the same day: however many regions the operator configures (optionally
// starting from meshmapper.net's own published boundary polygons), each
// a bounding box of its own boundary polygon.
//
// A point can be in MORE THAN ONE region now: boxes can overlap, and the
// backend fans out that way (confirmed live) — see regionsForPoint below.
// The anchor model forced exactly one nearest region; that assumption,
// and the "how far is too far" safety valve that went with it
// (hazards.region_max_distance_km — deleted from /api/config/schema, no
// longer read anywhere in this app), are both gone. A bbox simply excludes
// a point that isn't in it, which is itself the correct, sufficient
// answer — it cannot produce the anchor model's "nearest anchor happens to
// be on the wrong continent" failure mode, so there is nothing for a
// maximum distance to guard against here. Do not re-add one.
import type { RegionDef } from "../api/types";

export interface BBox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** A region's bounding box, from the confirmed live fields (see RegionDef's
 * own comment) — null only when one is missing or non-numeric, which every
 * caller below then correctly treats as "unknown geometry," never a wrong
 * guess. */
export function regionBBox(region: RegionDef): BBox | null {
  const minLat = num(region.min_lat);
  const maxLat = num(region.max_lat);
  const minLon = num(region.min_lon);
  const maxLon = num(region.max_lon);
  if (minLat == null || maxLat == null || minLon == null || maxLon == null) return null;
  return { minLat, maxLat, minLon, maxLon };
}

export function pointInBBox(lat: number, lon: number, box: BBox): boolean {
  return lat >= box.minLat && lat <= box.maxLat && lon >= box.minLon && lon <= box.maxLon;
}

/** Whether two boxes share any area — for a hazard event's own footprint
 * (e.g. a fire perimeter's bbox) against a region's box, the same rule the
 * backend's own item_fields description names explicitly ("for a
 * footprint, its bbox"). Not called from anywhere in this app today (no
 * hazard event carries a footprint geometry on the wire yet) — kept here
 * as the one place this rule is defined, ready for when one does, rather
 * than reinvented ad hoc later. */
export function bboxOverlap(a: BBox, b: BBox): boolean {
  return a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLon <= b.maxLon && a.maxLon >= b.minLon;
}

/** Names of every region in the given set a point falls inside — zero,
 * one, or more (boxes can overlap; see this file's own header comment).
 * Unlike the anchor model, box membership is independent per box, so
 * there is no need to classify against a wider candidate set than the one
 * actually being asked about — callers may pass just the regions they
 * care about (e.g. only the currently-selected ones) without risking a
 * wrong answer. */
export function regionsForPoint(lat: number | null | undefined, lon: number | null | undefined, regions: RegionDef[]): string[] {
  if (lat == null || lon == null) return [];
  const hits: string[] = [];
  for (const r of regions) {
    if (!r.name) continue;
    const box = regionBBox(r);
    if (box && pointInBBox(lat, lon, box)) hits.push(r.name);
  }
  return hits;
}

export interface RegionScopeArgs {
  selectedCount: number;
  regionsForNode: (n: { lat: number | null; lon: number | null }) => string[];
}

// Shared "is this thing in the current region selection" predicate — used
// by the map (dimming), the packet feed, the traffic histogram, and the
// status strip so all four agree on what "in this region" means instead of
// several subtly different reimplementations drifting apart. Originated in
// LiveMap.tsx's nodesToGeoJSON; pulled out here once a second consumer
// needed it.
//
// No region selected -> everything is in scope. Passing a real node whose
// position is known but whose region membership can't be resolved (no
// bbox contains it, or missing/invalid geometry) returns false here, same
// as the map: regionsForNode already returns [] in that case for every
// caller. The one deliberate difference from the map's own usage is `n`
// itself being null/undefined — e.g. a packet whose from_id doesn't
// resolve to any known node. The map never hits that case (it only ever
// calls this for nodes it is already iterating, which always have a
// position). A feed row or histogram bucket can, though, and here that
// counts as OUT of scope: defaulting an unresolvable packet to "in" would
// silently include unrelated traffic in a count the user asked to be
// region-scoped.
export function regionInScope(n: { lat: number | null; lon: number | null } | null | undefined, region: RegionScopeArgs): boolean {
  if (region.selectedCount === 0) return true;
  if (!n) return false;
  return region.regionsForNode(n).length > 0;
}

// Boundary-node treatment (spec §10: "Nodes near the region's boundary
// called out with a distinct marker outline ... so it's obvious who's
// sitting right at the edge and might be a candidate for reassignment or
// cross-region relay duty"). Plain degree deltas, not a projected
// Euclidean/geodesic distance — this is a soft visual "you're near an edge"
// cue, not an authoritative measurement, and these boxes are meshmapper's
// own coarse per-region boundary-polygon bounding boxes, not survey-grade
// edges. 0.1° is a chosen heuristic (~11km of latitude, ~8km of longitude
// at this project's ~43°N coverage), not a spec-mandated value — the spec
// gives none.
const BOUNDARY_THRESHOLD_DEG = 0.1;

function distanceToBoxEdge(lat: number, lon: number, box: BBox): number {
  const insideLat = lat >= box.minLat && lat <= box.maxLat;
  const insideLon = lon >= box.minLon && lon <= box.maxLon;
  const dLat = Math.min(Math.abs(lat - box.minLat), Math.abs(lat - box.maxLat));
  const dLon = Math.min(Math.abs(lon - box.minLon), Math.abs(lon - box.maxLon));
  if (insideLat && insideLon) return Math.min(dLat, dLon); // inside: distance to the nearest edge
  if (insideLat) return dLon; // outside east/west of the box only
  if (insideLon) return dLat; // outside north/south of the box only
  return Math.hypot(dLat, dLon); // outside both axes: nearest point is the corner, not either edge line
}

/** Whether a point sits within a soft threshold of any of the given
 * regions' boundary — used only against the currently *selected* regions
 * (a "region view" feature, per spec §10's own framing), never all
 * configured regions. */
export function nearAnyRegionBoundary(lat: number | null | undefined, lon: number | null | undefined, regions: RegionDef[]): boolean {
  if (lat == null || lon == null) return false;
  for (const r of regions) {
    const box = regionBBox(r);
    if (box && distanceToBoxEdge(lat, lon, box) <= BOUNDARY_THRESHOLD_DEG) return true;
  }
  return false;
}
